import { Box, Flex, Select } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FC, useCallback, useContext, useState } from "react";

import { ColumnDefinition, RanaTable } from "@nens/ranalib";
import { useTableData } from "@nens/react/hooks";
import * as api from "@nens/threedi/openapi-client";

import SimulationsSvg from "@/assets/simulations.svg?url";

import { getUsageApi } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import ShowTags from "../../components/ShowTags";
import { ThreediModelQueryOnClickLink } from "../../components/ThreediModelLink";
import { useUser } from "../../providers/AuthProvider";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { humanizeSeconds } from "../../utils/functions";

import UsageExcelButton from "./UsageExcelButton";
import UsageStatistics from "./UsageStatistics";

export type UsageRow = api.Usage & {
  uuid: string;
};

const columnDefinitions: ColumnDefinition[] = [
  {
    title: "Id",
    width: "table_cell_xxs",
  },
  {
    title: "Name",
    width: "table_cell_xs",
    orderingField: "simulation__name",
  },
  {
    title: "Model",
    width: "table_cell_xs",
    orderingField: "simulation__threedimodel__name",
  },
  {
    title: "User",
    width: "table_cell_xs",
    orderingField: "simulation__user__username",
  },
  {
    title: "Status",
    width: "table_cell_xxs",
  },
  {
    title: "Type",
    width: "table_cell_xxs",
    orderingField: "simulation_type",
  },
  {
    title: "Tags",
    width: "table_cell_xxs",
  },
  {
    title: "Started",
    width: "table_cell_xxs",
    orderingField: "started",
  },
  {
    title: "Length",
    width: "table_cell_xxs",
    orderingField: "total_time",
  },
  {
    title: "Project",
    width: "table_cell_xs",
  },
];

const Usage: FC = () => {
  const user = useUser();
  const { organisation } = useContext(OrganisationContext);
  const {
    limit,
    offset,
    setPageSize,
    setPage,
    page,
    searchString,
    setSearchString,
    searchField,
    setSearchField,
    orderings,
    setOrdering,
    orderingString,
  } = useTableData({
    initialPageSize: 20,
    initialSearchField: "simulation",
  });
  const [showOnlyMine, setShowOnlyMine] = useState<boolean>(false);
  const [typeIsLive, setTypeIsLive] = useState<boolean | undefined>(undefined);
  const [hideCrashed, setHideCrashed] = useState<boolean>(false);

  const setLiveOnly = useCallback((value: boolean) => setTypeIsLive(value ? true : undefined), []);
  const setApiOnly = useCallback((value: boolean) => setTypeIsLive(value ? false : undefined), []);
  const usageApi = getUsageApi();

  const usageQuery = (limit: number, offset: number) =>
    usageApi.usageList({
      simulationOrganisationUniqueId: organisation.uniqueId,
      limit,
      offset,
      simulationUserUsername: showOnlyMine ? user.username : undefined,
      simulationTypeLive: typeIsLive,
      simulationStatus: hideCrashed ? "finished" : undefined,
      simulationThreedimodelNameIcontains:
        searchField === "model" && searchString ? searchString : undefined,
      simulationUserUsernameIcontains:
        !showOnlyMine && searchField === "user" && searchString ? searchString : undefined,
      simulationNameIcontains:
        searchField === "simulation" && searchString ? searchString : undefined,
      simulationId:
        searchField === "simulationId" && searchString ? Number(searchString) : undefined,
      ordering: orderingString || undefined,
    });

  const simulationUsages = useQuery({
    queryKey: [
      "usages",
      organisation.uniqueId,
      showOnlyMine,
      limit,
      offset,
      typeIsLive,
      hideCrashed,
      searchString || null,
      searchString ? searchField : null,
      orderingString,
    ],
    queryFn: () => usageQuery(limit, offset),
  });

  const rows: UsageRow[] =
    simulationUsages.status === "success"
      ? simulationUsages.data.results.map((row) => ({
          ...row,
          uuid: "" + row.id,
        }))
      : [];

  const searchFields = [
    ["simulation", "Simulation name"],
    ["simulationId", "Simulation ID"],
    ["model", "Model name"],
    ["user", "Username"],
  ];

  return (
    <ExplainSideColumn
      imgUrl={SimulationsSvg}
      imgAltDescription={"Simulation icon"}
      headerText={"Recent Simulations"}
      explanationText={
        <UsageStatistics
          username={showOnlyMine ? user.username : undefined}
          typeIsLive={typeIsLive}
          hideCrashed={hideCrashed}
          modelNameSearch={searchField === "model" && searchString ? searchString : undefined}
          simulationNameSearch={
            searchField === "simulation" && searchString ? searchString : undefined
          }
          userNameSearch={searchField === "user" && searchString ? searchString : undefined}
        />
      }
      backUrl={"/"}
    >
      <Box mt={2}>
        <RanaTable
          sx={{
            // vertical alignment for the icons and checkboxes
            "& p": {
              marginBottom: 0,
            },
            "& label": {
              marginBottom: 0,
            },
            "& .chakra-checkbox__label": {
              fontWeight: 400,
              color: "brand.deep_sea",
            },
          }}
          columnDefinitions={columnDefinitions}
          tableDataIds={rows.map((row) => row.uuid)}
          isLoading={simulationUsages.status !== "success"}
          ordering={orderings}
          setOrdering={setOrdering}
          emptyElement={<RanaTable.Empty />}
        >
          <Flex justifyContent="space-between" mb={4}>
            <Flex>
              <Select
                value={searchField}
                onChange={(event) => setSearchField(event.target.value)}
                id="search-field-select"
                height="48px" // same as search-string-input
              >
                {searchFields.map(([value, description], idx) => (
                  <option key={`${value}-${idx}`} value={value}>
                    Search: {description}
                  </option>
                ))}
              </Select>
            </Flex>
            <Flex>
              <UsageExcelButton
                disabled={
                  !simulationUsages.isSuccess ||
                  !simulationUsages.data ||
                  !simulationUsages.data.results.length
                }
                usageQuery={usageQuery}
                organisationUniqueId={organisation.uniqueId}
              />
            </Flex>
          </Flex>
          <RanaTable.Filters
            search={{
              searchString,
              setSearchString,
              placeholder: "Type to search",
            }}
            checkboxes={[
              {
                label: "Show my simulations",
                checked: showOnlyMine,
                setChecked: setShowOnlyMine,
              },
              {
                label: "API simulations",
                checked: typeIsLive === false,
                setChecked: setApiOnly,
              },
              {
                label: "Rana Live simulations",
                checked: typeIsLive === true,
                setChecked: setLiveOnly,
              },
              {
                label: "Hide crashed simulations",
                checked: hideCrashed,
                setChecked: setHideCrashed,
              },
            ]}
          />
          <RanaTable.Container>
            <RanaTable.Header />
            <RanaTable.Body>
              {rows.map((row) => (
                <RanaTable.Row
                  to={
                    row.simulation?.id
                      ? `${APP_BASE_HREF}/simulations/${row.simulation.id}`
                      : undefined
                  }
                  key={row.uuid}
                  rowId={row.uuid}
                >
                  <RanaTable.Cell>{row.simulation?.id}</RanaTable.Cell>
                  <RanaTable.Cell>{row.simulation?.name}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <ThreediModelQueryOnClickLink simulation={row.simulation} />
                  </RanaTable.Cell>
                  <RanaTable.Cell title={row.userName}>{row.userName}</RanaTable.Cell>
                  <RanaTable.Cell>{row.status}</RanaTable.Cell>
                  <RanaTable.Cell title={row.simulationType}>{row.simulationType}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <ShowTags tags={row.simulation?.tags} />
                  </RanaTable.Cell>
                  <RanaTable.Cell>{row.started?.toLocaleDateString()}</RanaTable.Cell>
                  <RanaTable.Cell>{humanizeSeconds(row.totalTime ?? null)}</RanaTable.Cell>
                  <RanaTable.Cell>
                    <ShowTags
                      tags={row.simulation?.tags?.filter((tag) => tag.split(":")[0] === "project")}
                    />
                  </RanaTable.Cell>
                </RanaTable.Row>
              ))}
            </RanaTable.Body>
          </RanaTable.Container>
          <RanaTable.Pagination
            page={page}
            numPages={Math.ceil((simulationUsages.data?.count ?? 0) / limit)}
            limit={limit}
            setPageSize={setPageSize}
            setPage={setPage}
          />
        </RanaTable>
      </Box>
    </ExplainSideColumn>
  );
};

export default Usage;
